
import { useState, useEffect, useCallback } from 'react';

interface Timeslot {
  start_time: string;
  end_time: string;
}

interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: {
    stepDate: string;
    stepTime: string;
    stepConfirm: string;
    selectDate: string;
    selectTime: string;
    reviewBooking: string;
    selectedDate: string;
    selectedTime: string;
    modifyTime: string;
    customerName: string;
    customerPhone: string;
    notes: string;
    notesPlaceholder: string;
    confirmBooking: string;
    booking: string;
    success: string;
    error: string;
    noSlots: string;
    prev: string;
    next: string;
    back: string;
    sun: string;
    mon: string;
    tue: string;
    wed: string;
    thu: string;
    fri: string;
    sat: string;
  };
  containerStyle?: React.CSSProperties;
}

export default function BookingCalendar({ timeslotsApi, appointmentsApi, texts, containerStyle }: BookingCalendarProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [calendarMonth, setCalendarMonth] = useState<{ year: number; month: number }>({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [formData, setFormData] = useState({ customer_name: '', customer_phone: '', notes: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const dayNames = [texts.sun, texts.mon, texts.tue, texts.wed, texts.thu, texts.fri, texts.sat];

  const fetchTimeslots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(timeslotsApi);
      const data = await response.json();
      setTimeslots(data || []);
    } catch {
      setTimeslots([]);
    } finally {
      setLoading(false);
    }
  }, [timeslotsApi]);

  useEffect(() => {
    fetchTimeslots();
  }, [fetchTimeslots]);

  useEffect(() => {
    if (timeslots.length > 0) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      const availableDates = [...new Set(timeslots.map(slot => slot.start_time.split(' ')[0]))].sort();
      
      let targetDate = availableDates.find(date => date >= todayStr) || availableDates[availableDates.length - 1];
      
      if (targetDate) {
        const [year, month] = targetDate.split('-').map(Number);
        setCalendarMonth({ year, month: month - 1 });
        if (!selectedDate) {
          setSelectedDate(targetDate);
        }
      }
    }
  }, [timeslots, selectedDate]);

  const getAvailableDates = () => {
    return [...new Set(timeslots.map(slot => slot.start_time.split(' ')[0]))];
  };

  const getSlotsForDate = (date: string) => {
    return timeslots.filter(slot => slot.start_time.split(' ')[0] === date);
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const formatTime = (timeStr: string) => {
    const timePart = timeStr.split(' ')[1];
    if (!timePart) return '';
    const [hours, minutes] = timePart.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const getMonthName = (month: number) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month];
  };

  const handlePrevMonth = () => {
    setCalendarMonth(prev => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { year: prev.year, month: prev.month - 1 };
    });
  };

  const handleNextMonth = () => {
    setCalendarMonth(prev => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: prev.month + 1 };
    });
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setCurrentStep(2);
  };

  const handleSubmit = async () => {
    if (!selectedSlot || !formData.customer_name || !formData.customer_phone) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: formData.customer_name,
          customer_phone: formData.customer_phone,
          start_time: selectedSlot.start_time,
          end_time: selectedSlot.end_time,
          notes: formData.notes || ''
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        await fetchTimeslots();
        setTimeout(() => {
          setCurrentStep(0);
          setSelectedDate(null);
          setSelectedSlot(null);
          setFormData({ customer_name: '', customer_phone: '', notes: '' });
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCalendar = () => {
    const { year, month } = calendarMonth;
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const availableDates = getAvailableDates();

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isAvailable = availableDates.includes(dateStr);
      const isSelected = selectedDate === dateStr;

      days.push(
        <button
          key={day}
          onClick={() => isAvailable && handleDateSelect(dateStr)}
          disabled={!isAvailable}
          className={`h-10 w-full rounded-lg text-sm font-medium transition-all ${
            isSelected
              ? 'bg-amber-500 text-black'
              : isAvailable
              ? 'bg-neutral-800 text-white hover:bg-amber-500/30 cursor-pointer'
              : 'text-neutral-600 cursor-not-allowed'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const availableSlots = selectedDate ? getSlotsForDate(selectedDate) : [];

  if (loading) {
    return (
      <div style={containerStyle} className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Step Indicators */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {[texts.stepDate, texts.stepTime, texts.stepConfirm].map((step, index) => (
          <div key={index} className="flex items-center">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              currentStep === index 
                ? 'bg-amber-500 text-black' 
                : currentStep > index 
                ? 'bg-amber-500/20 text-amber-500' 
                : 'bg-neutral-800 text-neutral-500'
            }`}>
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-black/20 text-xs">
                {index + 1}
              </span>
              <span className="hidden sm:inline">{step}</span>
            </div>
            {index < 2 && <div className={`w-8 h-0.5 mx-1 ${currentStep > index ? 'bg-amber-500' : 'bg-neutral-700'}`}></div>}
          </div>
        ))}
      </div>

      {/* Step 0: Date Selection */}
      {currentStep === 0 && (
        <div>
          <h3 className="text-xl font-bold text-white mb-6 text-center">{texts.selectDate}</h3>
          
          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={handlePrevMonth} className="w-10 h-10 flex items-center justify-center rounded-lg bg-neutral-800 text-white hover:bg-neutral-700 transition-colors cursor-pointer">
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <span className="text-white font-semibold">{getMonthName(calendarMonth.month)} {calendarMonth.year}</span>
            <button onClick={handleNextMonth} className="w-10 h-10 flex items-center justify-center rounded-lg bg-neutral-800 text-white hover:bg-neutral-700 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-neutral-500">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-6">
            {renderCalendar()}
          </div>

          {/* Continue Button */}
          <button
            onClick={() => selectedDate && setCurrentStep(1)}
            disabled={!selectedDate}
            className="w-full bg-gradient-to-r from-amber-600 to-amber-500 text-black px-6 py-3 rounded-lg text-sm font-bold hover:from-amber-500 hover:to-amber-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {texts.next}
          </button>
        </div>
      )}

      {/* Step 1: Time Selection */}
      {currentStep === 1 && (
        <div>
          <button onClick={() => setCurrentStep(0)} className="flex items-center gap-2 text-amber-500 text-sm font-medium mb-4 hover:text-amber-400 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            {texts.back}
          </button>
          
          <h3 className="text-xl font-bold text-white mb-2 text-center">{texts.selectTime}</h3>
          <p className="text-center text-amber-500 text-sm mb-6">{selectedDate}</p>

          {availableSlots.length === 0 ? (
            <p className="text-center text-neutral-500 py-8">{texts.noSlots}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              {availableSlots.map((slot, index) => (
                <button
                  key={index}
                  onClick={() => handleSlotSelect(slot)}
                  className="px-4 py-3 rounded-lg bg-neutral-800 text-white text-sm font-medium hover:bg-amber-500 hover:text-black transition-all cursor-pointer"
                >
                  {formatTime(slot.start_time)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Review & Form */}
      {currentStep === 2 && selectedSlot && (
        <div>
          <button onClick={() => setCurrentStep(1)} className="flex items-center gap-2 text-amber-500 text-sm font-medium mb-4 hover:text-amber-400 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            {texts.modifyTime}
          </button>

          <h3 className="text-xl font-bold text-white mb-6 text-center">{texts.reviewBooking}</h3>

          {/* Review Section */}
          <div className="bg-neutral-800/50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-neutral-400 text-sm">{texts.selectedDate}</span>
              <span className="text-white font-medium">{selectedSlot.start_time.split(' ')[0]}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-400 text-sm">{texts.selectedTime}</span>
              <span className="text-white font-medium">
                {formatTime(selectedSlot.start_time)} - {formatTime(selectedSlot.end_time)}
              </span>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">{texts.customerName} *</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg bg-neutral-950 border border-neutral-700 text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">{texts.customerPhone} *</label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg bg-neutral-950 border border-neutral-700 text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">{texts.notes}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                maxLength={500}
                rows={3}
                className="w-full px-4 py-3 rounded-lg bg-neutral-950 border border-neutral-700 text-white placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none text-sm"
                placeholder={texts.notesPlaceholder}
              ></textarea>
            </div>

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.customer_name || !formData.customer_phone}
              className="w-full bg-gradient-to-r from-amber-600 to-amber-500 text-black px-6 py-4 rounded-lg text-base font-bold hover:from-amber-500 hover:to-amber-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? texts.booking : texts.confirmBooking}
            </button>

            {submitStatus === 'success' && (
              <div className="bg-amber-500/10 border border-amber-500/30 text-amber-500 px-4 py-3 rounded-lg text-sm text-center">
                {texts.success}
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm text-center">
                {texts.error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
